import React, { useContext, useEffect, useState } from "react";
import {
    Container, Card, Button, FormControl, InputGroup, Alert,
    Col, Table, Tabs, Tab
} from "react-bootstrap";
import { Context } from "../../index";
import Row from "react-bootstrap/Row";
import { fetchReferredUsers } from "../../http/userAPI";
import AffiliateTreeLog from "./AffiliateTreeLog";
import AllAffiliatesLog from "./AllAffiliatesLogs";
import RequestPartnerPromoCode from "./RequestPartnerPromoCode";
import SetAffiliateModel from "./SetAffiliateModel";
import AffiliatePayOutOrders from "./AffiliatePayOuts";
import AffiliateEarningsCalculator from "./AffiliateCalculator";
import ReferalLinksPanel from "./ReferalLinksPanel";

const PartnerDashboard = () => {
    const { user } = useContext(Context);
    const [referrals, setReferrals] = useState([]);
    const [key, setKey] = useState('instructions');

    const referralLink = `${window.location.origin}/?ref=${user.user.id}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink).then(() => {
            alert("Referral link copied to clipboard!");
        });
    };

    useEffect(() => {
        if (user?.user?.id) {
            fetchReferredUsers(user.user.id).then(setReferrals);
        }
    }, [user?.user?.id]);

    const formatDate = (minutes) => {
        const ms = minutes * 60 * 1000;
        const date = new Date(ms);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' }); // Apr
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const mins = String(date.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${mins}`;
    };


    return (
        <Card className="p-2 w-100 mt-2">
            <Row className="p-2">

                <Col>
                    <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3 nav-success" >
                        <Tab eventKey="instructions" title="Affiliate Program" className="">
                            <h5 className="text-muted">Affiliate Program</h5>
                            <div className="text-muted small">
                                <p className="mb-2">
                                    Welcome to the Virtalibry Affiliate Program! Here, you can earn rewards by inviting new users to the platform. There are several types of cooperation — from fast one-time bonuses to long-term multi-level reward models.
                                </p>

                                <p className="mb-2">
                                    On the <strong>Referral Link</strong> tab, you'll find your personal referral link — just share it with others to start earning.
                                    <br />
                                    On the <strong>Promo Codes</strong> tab, you can request a custom promo code (with your name or brand) that gives your referrals a discount.
                                    <br />
                                    On the <strong>Referral Tree</strong> tab, you’ll see a full structure of users on <em>Level 1, 2, and 3</em> — including all reward transactions (if your model supports multi-level rewards).
                                    <br />
                                    On the <strong>Affiliate Logs</strong> tab, you can view a complete list of all reward logs with filters by date.
                                    <br />
                                    The <strong>Settings</strong> tab lets you select and save the affiliate model that best fits your goals.
                                    <br />
                                    On the <strong>Payout</strong> tab, you can request a withdrawal of your earned rewards.
                                </p>
                                <p className="mb-3">
                                    Each tab helps you track your performance, maximize your referral potential, and manage your partnership with full control.
                                </p>
                                <p className="mb-2">
                                    Below is a quick overview of the available affiliate models. You can switch between them anytime in the <strong>Settings</strong> tab. Each model has its own logic:
                                </p>



                                <ul className="mb-3">
                                    <li><strong>VIRT</strong> – Earn internal currency (Virts). Great for getting more reviews. You get bonuses when users register and when they upgrade to a paid plan.</li>
                                    <li><strong>ONETIME-50</strong> – Earn 50% from a user’s first payment. One-time reward. Fastest option if you need quick results.</li>
                                    <li><strong>1Y-20</strong> – Multi-level model with 20% / 10% / 5% commissions over 1 year. Best for short-term aggressive growth.</li>
                                    <li><strong>2Y-15</strong> – 15% / 7.5% / 3.75% commissions for 2 years. Balanced long-term model with stable income potential.</li>
                                    <li><strong>3Y-10</strong> – 10% / 5% / 2.5% over 3 years. Ideal for those who want slower, but steady growth.</li>
                                    <li><strong>5Y-5</strong> – 5% / 2.5% / 1.25% over 5 years. Lowest monthly %, but long-lasting passive rewards.</li>
                                </ul>


                                <AffiliateEarningsCalculator />

                                <p className="mt-3 mb-1"><strong>NOTE:</strong></p>
                                <p>
                                    <em>Important to understand:</em> The affiliate model you choose applies <strong>only to users who register after you change the model</strong>. It does <strong>not</strong> affect users who registered earlier.
                                    <br />
                                    For example: let’s say you selected the <strong>1Y-20</strong> model and invited user A. Later, you switched to <strong>ONETIME-50</strong> and invited user B.
                                    <br />
                                    In this case, you will continue to receive long-term rewards from user A based on the 1Y-20 model, and you will get a one-time reward from user B (50% of their first payment).
                                    <br />
                                    <br />
                                    There is <strong>no limit</strong> to how many times you can change your affiliate model. Feel free to experiment and choose what works best for you!
                                </p>
                            </div>



                        </Tab>

                        <Tab eventKey="link" title="Referral Link">
                            <ReferalLinksPanel referralLink={referralLink} />



                        </Tab>
                        <Tab eventKey="promo_codes" title="Promo Codes">
                            <RequestPartnerPromoCode />

                        </Tab>

                        <Tab eventKey="tree" title="Referral Tree">
                            <AffiliateTreeLog userId={user?.user?.id} />
                        </Tab>

                        <Tab eventKey="aff_all_logs" title="Affiliate Logs">
                              <AllAffiliatesLog userId={user?.user?.id} />
                        </Tab>
                        <Tab eventKey="settings" title="Settings">
                            <SetAffiliateModel userId={user?.user?.id} />
                        </Tab>

                        <Tab eventKey="pay_out" title="Pay Out">
                            <AffiliatePayOutOrders userId={user?.user?.id}/>

                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Card>
    );
};

export default PartnerDashboard;
